/*
Copyright (C) 2009 - 2026 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC, useState, useRef, useEffect } from 'react';
import { map } from 'lodash';
import cx from 'classnames';
import { Button } from '@broadleaf/admin-components/dist/common/index';
import { getAttribute } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';

export function formatGroupTypeLabel(groupType: string): string {
  if (!groupType) return '';

  const upper = groupType.toUpperCase();
  if (upper === 'EXPORT') return 'Export';
  if (upper === 'IMPORT') return 'Import';
  if (upper === 'BATCH' || upper === 'BATCH_UPDATE') return 'Batch';

  const words = groupType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/);

  return words
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export interface GridActionGroupDropdownProps {
  groupType: string;
  actionDefinitions: any[];
  gridProps: any;
  renderSingleAction: (
    actionDefinition: any,
    key: string | number,
    props: any
  ) => React.ReactNode;
}

export const GridActionGroupDropdown: FC<GridActionGroupDropdownProps> = ({
  groupType,
  actionDefinitions,
  gridProps,
  renderSingleAction
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const label =
    getAttribute(actionDefinitions[0], 'groupLabel') ||
    formatGroupTypeLabel(groupType);

  return (
    <div ref={containerRef} className="text-left tw-relative tw-inline-block">
      <Button
        type="button"
        size="sm"
        className="tw-inline-flex tw-items-center tw-justify-between tw-gap-1"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span>{label}</span>
        <span className="tw-ml-1 tw-text-xs">▾</span>
      </Button>

      <div
        className={cx(
          'tw-absolute tw-right-0 tw-z-50 tw-mt-1 tw-min-w-[160px] tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-py-1 tw-shadow-lg',
          { 'tw-hidden': !isOpen }
        )}
      >
        {map(actionDefinitions, (actionDefinition, index) => (
          <div
            key={index}
            className="grid-action-dropdown-item tw-w-full"
            onClickCapture={e => {
              const target = e.target as HTMLElement;
              if (
                target.tagName === 'BUTTON' ||
                target.tagName === 'A' ||
                target.closest('button') ||
                target.closest('a') ||
                target.getAttribute('role') === 'button'
              ) {
                setIsOpen(false);
              }
            }}
          >
            {renderSingleAction(actionDefinition, index, gridProps)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GridActionGroupDropdown;
